import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { DashboardPipeline } from "@/features/dashboard/api/types"
import { formatNumber, kgToMt } from "@/lib/format"
import { cn } from "@/lib/utils"

type Tone = "slate" | "indigo" | "amber" | "sky" | "violet" | "emerald"

interface Stage {
  key: keyof DashboardPipeline
  label: string
  tone: Tone
}

const toneStyles: Record<Tone, string> = {
  slate: "bg-slate-100 text-slate-800 dark:bg-slate-500/15 dark:text-slate-200",
  indigo:
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-200",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  sky: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-200",
  violet:
    "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-200",
  emerald:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200",
}

interface PipelineStripProps {
  pipeline: DashboardPipeline
}

export function PipelineStrip({ pipeline }: PipelineStripProps) {
  const { t } = useTranslation("dashboard")

  const stages: Stage[] = [
    { key: "contractedKg", label: t("pipeline.contracted"), tone: "indigo" },
    { key: "orderedKg", label: t("pipeline.ordered"), tone: "slate" },
    { key: "inTransitKg", label: t("pipeline.inTransit"), tone: "amber" },
    { key: "receivedKg", label: t("pipeline.received"), tone: "sky" },
    {
      key: "lotsAvailableKg",
      label: t("pipeline.lotsAvailable"),
      tone: "violet",
    },
    { key: "shippedKg", label: t("pipeline.shipped"), tone: "emerald" },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("pipeline.title")}</CardTitle>
        <p className="text-muted-foreground text-xs">{t("pipeline.subtitle")}</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {stages.map((stage) => (
            <div
              key={stage.key}
              className={cn(
                "flex flex-col gap-1 rounded-lg px-3 py-2.5",
                toneStyles[stage.tone],
              )}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
                {stage.label}
              </span>
              <span className="text-xl font-semibold tabular-nums">
                {formatNumber(kgToMt(pipeline[stage.key]), 1)}
                <span className="ml-1 text-xs font-medium opacity-70">MT</span>
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
